// Copyright (C) 2021-2022 Intel Corporation
//
// SPDX-License-Identifier: MIT

import { ImageProcessing } from './opencv-interfaces';

export interface HistogramEqualization extends ImageProcessing {
    processImage: (src: ImageData, frameNumber: number) => ImageData;
}

interface HashedImage {
    frameNumber: number;
    frameData: ImageData;
    timestamp: number;
}

export default class VisAutoContrastImplementation implements HistogramEqualization {
    private readonly bufferSize: number = 20;
    private cv: any;
    private histHash: HashedImage[];
    public currentProcessedImage: number | undefined;

    constructor(cv: any) {
        this.cv = cv;
        this.histHash = [];
    }

    // orig_dtype = image.dtype
    // M = 255
    // if orig_dtype == np.uint16:
    //     M = 2 ** 16 - 1
    // elif orig_dtype == np.int16:
    //     M = 2 ** 15 - 1
    // image -= image.min()
    // image = image.astype(float)
    // if gamma != 1.0:
    //     image = image**gamma
    // image *= (M / image.max())
    // image = image.astype(orig_dtype)

    // public processImage(src: ImageData, frameNumber: number): ImageData {
    //     console.log(src);
    //     const hashedFrame = this.hashedFrame(frameNumber);
    //     if (!hashedFrame) {
    //         const { cv } = this;
    //         let matImage = null;
    //         const RGBImage = new cv.Mat();
    //         const RGBADist = new cv.Mat();

    //         try {
    //             this.currentProcessedImage = frameNumber;
    //             matImage = cv.matFromImageData(src);

    //             cv.cvtColor(matImage, RGBImage, cv.COLOR_RGBA2RGB, 0);

    //             let min = Infinity;
    //             let max = -Infinity;
    //             for (let i = 0; i < RGBImage.data.length; i++) {
    //                 if (RGBImage.data[i] < min) {
    //                     min = RGBImage.data[i];
    //                 }
    //                 if (RGBImage.data[i] > max) {
    //                     max = RGBImage.data[i];
    //                 }
    //             }

    //             const subMat = new cv.Mat(RGBImage.rows, RGBImage.cols, cv.CV_8UC3, new cv.Scalar(min, min, min));
    //             cv.subtract(RGBImage, subMat, RGBImage);

    //             const M = 255;
    //             const value = M / max;
    //             const multiplyMat = new cv.Mat(
    //                 RGBImage.rows,
    //                 RGBImage.cols,
    //                 cv.CV_8UC3,
    //                 new cv.Scalar(value, value, value),
    //             );
    //             cv.multiply(RGBImage, multiplyMat, RGBImage);

    //             cv.cvtColor(RGBImage, RGBADist, cv.COLOR_RGB2RGBA, 0);
    //             const arr = new Uint8ClampedArray(RGBADist.data, RGBADist.cols, RGBADist.rows);
    //             const imgData = new ImageData(arr, src.width, src.height);

    //             this.hashFrame(imgData, frameNumber);
    //             return imgData;
    //         } catch (e) {
    //             throw new Error(e.toString());
    //         } finally {
    //             if (matImage) matImage.delete();
    //             RGBImage.delete();
    //             RGBADist.delete();
    //         }
    //     } else {
    //         this.currentProcessedImage = frameNumber;
    //         return hashedFrame;
    //     }
    // }

    public processImage(src: ImageData, frameNumber: number): ImageData {
        console.log(src);
        const { cv } = this;
        let matImage = null;
        const RGBImage = new cv.Mat();
        const RGBADist = new cv.Mat();
        const hashedFrame = this.hashedFrame(frameNumber);
        const gamma = 1;

        try {
            this.currentProcessedImage = frameNumber;
            matImage = cv.matFromImageData(src);
            cv.cvtColor(matImage, RGBImage, cv.COLOR_RGBA2RGB, 0);

            let min = Infinity;
            let max = -Infinity;
            for (let i = 0; i < RGBImage.data.length; i++) {
                if (RGBImage.data[i] < min) {
                    min = RGBImage.data[i];
                }
                if (RGBImage.data[i] > max) {
                    max = RGBImage.data[i];
                }
            }

            const subMat = new cv.Mat(RGBImage.rows, RGBImage.cols, cv.CV_8UC3, new cv.Scalar(min, min, min));
            cv.subtract(RGBImage, subMat, RGBImage);

            // Equivalent to orig_dtype in the Python code
            let M: number = 255;

            // Handling gamma transformation, equivalent to the Python code
            // if (gamma !== 1.0) {
            for (let i = 0; i < RGBImage.data.length; i++) {
                RGBImage.data[i] = Math.pow(RGBImage.data[i], gamma);
            }
            // }

            const value = M / max;
            const multiplyMat = new cv.Mat(
                RGBImage.rows,
                RGBImage.cols,
                cv.CV_8UC3,
                new cv.Scalar(value, value, value),
            );
            cv.multiply(RGBImage, multiplyMat, RGBImage);

            cv.cvtColor(RGBImage, RGBADist, cv.COLOR_RGB2RGBA, 0);
            const arr = new Uint8ClampedArray(RGBADist.data, RGBADist.cols, RGBADist.rows);
            const imgData = new ImageData(arr, src.width, src.height);

            return imgData;
        } catch (e) {
            throw new Error(e.toString());
        } finally {
            if (matImage) matImage.delete();
            RGBImage.delete();
            RGBADist.delete();
        }
    }

    private hashedFrame(frameNumber: number): ImageData | null {
        const hashed = this.histHash.find((_hashed) => _hashed.frameNumber === frameNumber);
        if (hashed) {
            hashed.timestamp = Date.now();
        }
        return hashed?.frameData || null;
    }

    private hashFrame(frameData: ImageData, frameNumber: number): void {
        if (this.histHash.length >= this.bufferSize) {
            const leastRecentlyUsed = this.histHash[0];
            const currentTimestamp = Date.now();
            let diff = currentTimestamp - leastRecentlyUsed.timestamp;
            let leastIndex = 0;
            for (let i = 1; i < this.histHash.length; i++) {
                const currentDiff = currentTimestamp - this.histHash[i].timestamp;
                if (currentDiff > diff) {
                    diff = currentDiff;
                    leastIndex = i;
                }
            }
            this.histHash.splice(leastIndex, 1);
        }
        this.histHash.push({
            frameData,
            frameNumber,
            timestamp: Date.now(),
        });
    }
}
